// import { isEmpty, ensureSinglePhraseId, mean, generateObjectId } from '../utils';
import { post_auth } from '../auth';
import { serverUrl } from '../ConfigHolder';
import { isEmpty, mean } from 'lodash';

// ------------------------------------------------------
// Base Sentence wrapper for any phrase object
//
// IMPORTANT TERMINOLOGY
//   - phraseVal.history and phraseVal.bestScore live on the underlying
//     phrase object and are treated here as **global, persisted metrics**
//     (coming from MongoDB) that aggregate attempts across sessions.
//   - VoiceRecorder keeps separate, in-memory sessionAttempts/sessionBestScore
//     for the current tab. Those are used to update these global fields, but
//     are not stored directly on Sentence.
// ------------------------------------------------------
export class Sentence {
  constructor(content = {}, index = -1, {dontUpdateContent = false, 
                                         play = true, 
                                         ID = null} = {},) {
    if (!isEmpty(content)) {
      this.play = play;
      this.dontUpdateContent = dontUpdateContent;
      this.contentObjId = content._id ?? null;
      this.userId = content.userId ?? null;
      this.Learn = content.Learn ?? null;
      this.Base = content.Base ?? null;
      this.contentType = content.content ?? null;
      
      if (index !== -1) {
          this.index = index;
          this.phraseObj = content.phrases?.[index]
        ? JSON.parse(JSON.stringify(content.phrases[index]))
        : {};
      } else {
        for (let i = 0; i < content.phrases.length; i++) {
          if (ID === content.phrases[i]._id) {
            this.index = i;
            this.phraseObj = content.phrases?.[i]
              ? JSON.parse(JSON.stringify(content.phrases[i])): {};
            break;
          }
        }
      }
     

      // Guarantee phraseVal and history exist
      if (!this.phraseObj.phraseVal) this.phraseObj.phraseVal = {};
      if (!('history' in this.phraseObj.phraseVal))
        this.phraseObj.phraseVal.history = [];
    } else {
      this.phraseObj = {};
      this.play = null;
      this.contentObjId = null;
      this.userId = null;
      this.Learn = null;
      this.Base = null;
      this.contentType = null;
      this.index = -1;
    }
  }

  // Static methods

  // Generate a MongoDB-like ObjectId (24 hex chars: 8 for timestamp + 16 random hex)
  static generateObjectId() {
    const timestamp = Math.floor(Date.now() / 1000).toString(16).padStart(8, '0');
    const random = Array.from({ length: 16 }).map(() => Math.floor(Math.random() * 16).toString(16)).join('');
    return (timestamp + random).toLowerCase();
  };
  
  static ensurePhraseIds(obj) {
    if (!obj || !Array.isArray(obj.phrases)) return;
    for (const phraseObj of obj.phrases) {
      Sentence.ensureSinglePhraseId(phraseObj);
    }
  }
  
  static ensureSinglePhraseId(phraseObj) {
    if (!phraseObj) return;
    if (!phraseObj._id) {
        phraseObj._id = Sentence.generateObjectId();
      }
    return;
  }

  static avScore (phraseObj) {
    const LATEST_ATTEMPTS = 3;
    const rawHistory = phraseObj?.phraseVal?.history;

    // Ensure we have an array (or return null if nothing usable)
    const histarray = Array.isArray(rawHistory) ? rawHistory : (rawHistory ? [rawHistory] : []);
    if (histarray.length === 0) return null;

    // Map to numeric confidences and drop non-numbers
    const confarray = histarray
      .map(v => {
        const c = v?.confidence;
        return (typeof c === 'string' || typeof c === 'number') ? Number(c) : NaN;
      })
      .filter(n => !isNaN(n));

    if (confarray.length === 0) return null;

    const hmean = mean(confarray.slice(-LATEST_ATTEMPTS));
    return hmean;
  }
  
  // ----------------------------
  // GETTERS AND SETTERS
  // ----------------------------

  // The visible "sentence" text
  get sentence() {
    return this.phraseObj?.phrase ?? '';
  }

  set sentence(val) {
    if (this.phraseObj) this.phraseObj.phrase = val;
  }

  // Direct access to phraseVal for convenience
  get phraseVal() {
    return this.phraseObj?.phraseVal ?? {};
  }

  set phraseVal(val) {
    if (this.phraseObj) this.phraseObj.phraseVal = val ?? {};
  }

  // Access tags through a virtual property
  get tags() {
    return this.phraseObj?.tags ? [...this.phraseObj.tags] : [];
  }

  set tags(tagList) {
    if (this.phraseObj) this.phraseObj.tags = [...tagList];
  }

  // Explanations (grammar/phonetics etc.)
  get explanations() {
    const exps = this.phraseVal.explanations;
    return Array.isArray(exps) ? exps : [];
  }

  set explanations(list) {
    if (!this.phraseObj) return;
    if (!this.phraseObj.phraseVal) this.phraseObj.phraseVal = {};
    this.phraseObj.phraseVal.explanations = Array.isArray(list) ? list : [];
  }

  // Derived / computed properties
  get history() {
    return this.phraseVal.history ?? [];
  }

  get bestScore() {
    return this.phraseVal.bestScore ?? null;
  }

  set bestScore(val) {
    if (this.phraseObj?.phraseVal)
      this.phraseObj.phraseVal.bestScore = val;
  }

  get ID() {
    if (!this.phraseObj._id)
      this.phraseObj._id = Sentence.generateObjectId();
    return this.phraseObj._id;
  }

  get translation() {
    return this.phraseObj.phraseVal?.translation ?? null;
  }

  // ----------------------------
  // CORE METHODS
  // ----------------------------

  deepCopy() {
    const newSentence = new Sentence();
    newSentence.play = this.play;
    newSentence.Learn = this.Learn;
    newSentence.Base = this.Base;
    newSentence.phraseObj = JSON.parse(JSON.stringify(this.phraseObj));
    newSentence.sessionBestScore = this?.sessionBestScore || {}
    Sentence.ensureSinglePhraseId(newSentence.phraseObj);
    newSentence.contentObjId = this.contentObjId;
    newSentence.userId = this.userId;
    newSentence.contentType = this.contentType;
    newSentence.index = this.index;
    return newSentence;
  }

  static createPhraseWrap(phraseObj) {
    const ns = new Sentence();
    ns.play = true;
    ns.phraseObj = JSON.parse(JSON.stringify(phraseObj));
    Sentence.ensureSinglePhraseId(ns.phraseObj);
    return ns;
  }

  updateAttributes(content, index) {
    this.contentObjId = content._id ?? null;
    this.userId = content.userId ?? null;
    this.Learn = content.Learn ?? null;
    this.Base = content.Base ?? null;
    this.contentType = content.content ?? null;
    this.index = index;
  }

  getSentence() {
    if (this.contentType === 'numbers' || this.contentType === 'numbers/cardinal')
      return this.translation;
    return this.sentence;
  }

  get translation() {
    return this.phraseVal.translation ?? '';
  }

  getPath(gender) {
    const g = gender?.toUpperCase();
    return this.phraseVal.voices?.[g]?.path ?? null;
  }

  getDuration(gender) {
    const g = gender?.toUpperCase();
    return this.phraseVal.voices?.[g]?.duration ?? null;
  }

  // GLOBAL: number of stored attempts in phraseVal.history
  getGlobalAttemptCount() {
    return this.history.length;
  }

  // GLOBAL: average confidence across all stored attempts
  getGlobalAverageScore() {
    if (!this.history.length) return 0;
    const confidence = this.history.map(v => v.confidence);
    const m = mean(confidence) * 100;
    return m.toFixed(0);
  }

  // GLOBAL: best (max) score recorded in phraseVal.bestScore
  getGlobalMaxScore() {
    const bs = this.bestScore;
    return bs ? (bs.maxScore * 100).toFixed(0) : null;
  }

  // GLOBAL: shortest duration for the best scoring attempt
  getGlobalShortestDuration() {
    const bs = this.bestScore;
    return bs?.durMin ? `${bs.durMin.toFixed(2)} sec` : null;
  }

  // GLOBAL: path to audio file for the best scoring attempt
  getGlobalBestScorePath() {
    return this.bestScore?.fpathMaxscore ?? null;
  }

  getFemaleVoicePath() {
    return this.phraseVal.voices?.FEMALE?.path ?? null;
  }

  getMaleVoicePath() {
    return this.phraseVal.voices?.MALE?.path ?? null;
  }

  getGlobalBestScoreTime() {
    return this.bestScore?.timestamp ?? null;
  }

  updateSentence(bestScore, latestResult) {
    this.phraseVal.history.push(latestResult);
    this.phraseVal.attemptCount = this.phraseVal.history.length;
    this.sessionBestScore = bestScore;
  }

  addExplanation(explObj) {
    if (!this.phraseVal.explanations)
      this.phraseVal.explanations = [];

    const exists = this.phraseVal.explanations
      .some(e => e.explanation === explObj.explanation);

    if (!exists) {
      this.phraseVal.explanations.push(explObj);
      return true;
    }
    return false;
  }

  async saveSentence() {
    const data = {
      key: this.sentence,
      val: this.phraseVal,
      _id: this.ID,
      session_best: this.sessionBestScore,
      contentObjId: this.contentObjId,
      userId: this.userId,
      Learn: this.Learn,
      Base: this.Base,
      content: this.contentType,
      tags: this.tags
    };
    const uri = `${serverUrl}/save_phrase_object`;
    try {
      const response = await post_auth(uri, data);
      return response.data;
    } catch (err) {
      console.error(`Error updating phrase ${this.sentence}`, err);
      throw err;
    }
  }

  async addSentenceDB() {
    Sentence.ensureSinglePhraseId(this.phraseObj);
    const data = {
      contentObjId: this.contentObjId,
      userId: this.userId,
      Learn: this.Learn,
      Base: this.Base,
      content: this.contentType,
      index: this.index,
      phraseObj: this.phraseObj,
    };
    const uri = `${serverUrl}/add_phrase_object`;
    try {
      const response = await post_auth(uri, data);
      this.bestScore = response.data?.bestScore ?? {};
      if (response.data?.entry_timestamp)
        this.phraseVal.entry_timestamp = response.data.entry_timestamp;
    } catch (err) {
      console.error(`Error adding phrase ${this.sentence}`, err);
    }
  }

  async deleteSentenceDB() {
    const data = {
      contentObjId: this.contentObjId,
      userId: this.userId,
      phrase: this.sentence,
      phraseVal: this.phraseVal
    };
    const uri = `${serverUrl}/delete_phrase_object`;
    try {
      await post_auth(uri, data);
    } catch (err) {
      console.error(`Error deleting phrase ${this.sentence}`, err);
    }
  }

  hasMembers() {
    return Object.keys(this).length > 0;
  }
}

// ------------------------------------------------------
// MyConversationSentence (user-created conversations)
// ------------------------------------------------------
// This was formerly named ConversationSentence and is used for
// custom-content conversations that live inside the custom content doc.
export class MyConversationSentence extends Sentence {
  constructor(content = {}, index = -1, options = {}) {
    super(content, index, options);
    this.isConvo = true;
  }

  get gender() {
    return this?.phraseVal?.voices?.conversation_voice?.gender;
  }

  set gender(g) {
    if (this?.phraseVal?.voices?.conversation_voice) {
      this.phraseVal.voices.conversation_voice.gender = g;
    }
  }

  getPath(gender) {
    // MyConversationSentence is responsible for object-based conversation_voice.
    // If conversation_voice is an object, use its path; otherwise defer to base.
    const conv = this.phraseObj?.phraseVal?.voices?.conversation_voice;
    if (conv && typeof conv === 'object') {
      return conv.path || null;
    }
    return super.getPath(gender);
  }

  getDuration(gender) {
    const conv = this.phraseObj?.phraseVal?.voices?.conversation_voice;
    if (conv && typeof conv === 'object') {
      return conv.duration || null;
    }
    return super.getDuration(gender);
  }

  // Unified 1-voice interface for conversations (MyContent conversations)
  // Prefer an explicit conversation_voice object if present; otherwise
  // fall back to the gender-specific voice indicated by that field.
  getConversationVoice() {
    const voices = this.phraseVal?.voices;
    if (!voices) return null;

    const conv = voices.conversation_voice;

  // If conversation_voice is a full voice object, return it directly
  if (conv && typeof conv === 'object' && conv.path) {
      return conv;
    }

    return null;
  }

  deepCopy() {
    // Use base class deepCopy to clone common fields
    const baseCopy = super.deepCopy();
    // Convert to MyConversationSentence instance
    const cs = new MyConversationSentence();
    // Copy over all base fields
    Object.assign(cs, baseCopy);
    // Ensure conversation-specific flag
    cs.isConvo = true;

    // Ensure phraseVal.voices and conversation_voice are deep-copied if present
    if (this.phraseVal?.voices?.conversation_voice) {
      // Ensure phraseObj exists
      if (!cs.phraseObj) cs.phraseObj = {};
      if (!cs.phraseObj.phraseVal) cs.phraseObj.phraseVal = {};
      if (!cs.phraseObj.phraseVal.voices) cs.phraseObj.phraseVal.voices = {};
      cs.phraseObj.phraseVal.voices.conversation_voice = JSON.parse(
        JSON.stringify(this.phraseVal.voices.conversation_voice)
      );
    }

    // Ensure the copied phraseObj has an _id
    Sentence.ensureSinglePhraseId(cs.phraseObj);
    return cs;
  }
}

// ------------------------------------------------------
// ConversationSentence (predefined conversations)
// ------------------------------------------------------
// Wraps a canonical phrase plus optional user override object.
export class ConversationSentence extends MyConversationSentence {
  constructor(content = {}, index = -1, options = {}) {
    super(content, index, options);
  }
  // For predefined conversations, conversation_voice may be a string gender
  // (e.g. "MALE"/"FEMALE") selecting between the two-voice interface, or
  // a full voice object. String-based branching lives here.

  get conversationGender() {
    const conv = this.phraseVal?.voices?.conversation_voice;
    if (typeof conv === 'string') {
      return conv.toUpperCase();
    }
    // Fallback/default
    return 'FEMALE';
  }

  get gender() {
    return this.conversationGender;
  }

  getPath(gender) {
    const voices = this.phraseVal?.voices;
    if (!voices) return super.getPath(gender);

    const conv = voices.conversation_voice;
    // If it's a string, map through the 2-voice interface
    if (typeof conv === 'string') {
      const g = conv.toUpperCase();
      return voices[g]?.path ?? null;
    }

    // Otherwise defer to MyConversationSentence (object-based handling / base)
    return super.getPath(gender);
  }

  getDuration(gender) {
    const voices = this.phraseVal?.voices;
    if (!voices) return super.getDuration(gender);

    const conv = voices.conversation_voice;
    if (typeof conv === 'string') {
      const g = conv.toUpperCase();
      return voices[g]?.duration ?? null;
    }

    return super.getDuration(gender);
  }

  // For predefined conversations, provide a 1-voice interface that may be
  // backed either by a concrete conversation_voice object or by a gender
  // string selecting from the 2-voice map.
  getConversationVoice() {
    const voices = this.phraseVal?.voices;
    if (!voices) return null;

    const conv = voices.conversation_voice;

    // If it's a full object, let MyConversationSentence handle it
    if (conv && typeof conv === 'object' && conv.path) {
      return super.getConversationVoice();
    }

    // If it's a string gender, resolve via the two-voice interface
    if (typeof conv === 'string') {
      const g = conv.toUpperCase();
      return voices[g] || null;
    }

    return null;
  }
  
  // Ensure deep copies remain ConversationSentence instances, not
  // MyConversationSentence. Mirrors MyConversationSentence.deepCopy
  // but constructs a ConversationSentence for the result.
  deepCopy() {
    // Use base class deepCopy to clone common fields
    const baseCopy = super.deepCopy();
    // Convert to ConversationSentence instance
    const cs = new ConversationSentence();
    // Copy over all base fields
    Object.assign(cs, baseCopy);
    // Ensure conversation-specific flag
    cs.isConvo = true;

    // Ensure phraseVal.voices and conversation_voice are deep-copied if present
    if (this.phraseVal?.voices?.conversation_voice) {
      if (!cs.phraseObj) cs.phraseObj = {};
      if (!cs.phraseObj.phraseVal) cs.phraseObj.phraseVal = {};
      if (!cs.phraseObj.phraseVal.voices) cs.phraseObj.phraseVal.voices = {};
      cs.phraseObj.phraseVal.voices.conversation_voice = JSON.parse(
        JSON.stringify(this.phraseVal.voices.conversation_voice)
      );
    }

    // Ensure the copied phraseObj has an _id
    Sentence.ensureSinglePhraseId(cs.phraseObj);
    return cs;
  }
  
  // ---------------------------------------------
  // SAVE OVERRIDE (predefined conversations)
  // ---------------------------------------------
  async saveSentence() {
    // Merge any session best score into phraseVal.bestScore before send

    const phraseVal = this.phraseVal || {};

    const data = {
      key: this.sentence,
      val: phraseVal,
      _id: this.ID,
      session_best: this.sessionBestScore,
      contentObjId: 'predefined_conversations',
      userId: this.userId,
      Learn: this.Learn,
      Base: this.Base,
      content: 'conversations',
      tags: this.tags,
      // canonical identifiers that backend uses for overrides
      canonical_id: this.phraseObj?.canonical_id,
      conv_canonical_id: this.phraseObj?.conv_canonical_id,
      conv_version: this.phraseObj?.conv_version,
    };

    const uri = `${serverUrl}/save_phrase_object`;
    try {
      const response = await post_auth(uri, data);
      return response.data;
      // if (response.data?.bestScore)
      //   this.bestScore = response.data.bestScore;
    } catch (err) {
      console.error(`Error updating conversation phrase ${this.sentence}`, err);
      throw err;
    }
  }
};